import React from 'react';

import { Block, withConfig } from '../common/Block.jsx';
import { EditorContainer, EmptyContentPlaceholder, EmptyPlaceholder } from '../layout/Builder.jsx';
import Defaults from '../layout/Defaults.jsx';
import { default as Layout } from '../common/Layout.jsx';
import Name from '../common/Name.jsx';
import PageType from '../common/PageType.jsx';
import { Tab, TabsContainer, TabSelectionDropDown, TabV2 } from '../common/Tab.jsx';
import Token from '../common/Token.jsx';
import Types from '../common/Types.jsx';
import Utilities from '../common/Utilities.jsx';

// region TABS V1 (-- DEPRECATED use `TabsV2` --)

function TabsV1Component(props) {
    const [activeTab, setActiveTab] = React.useState(undefined);
    const handleTabClick = (tab, updateLocation) => {
        if (!tab) {
            setActiveTab(undefined);
            return;
        }
        if (updateLocation != false && location && !props.builder) {
            // Update hash for new active tab
            if (tab.anchorName) {
                location.hash = `#${tab.anchorName}`;
            } else {
                location.hash = '';
            }
        }
        setActiveTab(tab.id);
    }
    React.useEffect(() => {
        if (props.active && props.layoutContainerTabId) {
            setActiveTab(props.layoutContainerTabId);
        }
    }, []);
    React.useEffect(() => {
        if (props.active && props.onLayoutContainerTabSelection) {
            props.onLayoutContainerTabSelection(props.layoutContainerTabId || activeTab);
        }
    }, [props.active]);

    let _tabComponents;
    const [tabComponents, setTabComponents] = React.useState();
    const prevTabsRef = React.useRef();
    React.useEffect(() => { prevTabsRef.current = tabComponents && tabComponents.map((d) => d.tab && d.tab.id) }, []);

    React.useEffect(() => {
        if (!props.data) {
            return;
        }
        let tabComponentList = [];
        if (props.blockConfig.tabs) {
            // Determine visibility of each tab based on its contents
            props.blockConfig.tabs.forEach((t) => {
                if (!t.tabContent || !t.tabContent.desktop || !t.tabContent.desktop.blocks || t.tabContent.desktop.blocks.length == 0) {
                    // No content blocks for tab => Don't process any further (tab is not visible)
                    return;
                }

                // Map each content block to component state visibility representation
                const tabId = t.id;
                const tabContent = { components: {}, tab: { ...t }, visible: props.builder };
                if (!tabContent.tab.anchorName) {
                    // Set a default anchor
                    tabContent.tab.anchorName = tabContent.tab.display ? tabContent.tab.display.toLowerCase() : tabId;
                }
                t.tabContent.desktop.blocks.forEach((d) => {
                    // Get block component empty state
                    const componentState = Types.Block.isComponentEmpty(d, props.data, props.config);
                    if (!componentState) {
                        // Something went wrong
                        console.log(`Unable to get block component empty state: `);
                        console.log(d);
                        return;
                    }
                    // Add all component states to the components list for the current tab
                    componentState.forEach((temp) => {
                        tabContent.components[temp.id] = temp;
                        tabContent.visible = tabContent.visible || temp.visible;
                    });
                })
                tabComponentList.push(tabContent);
            });
        }
        _tabComponents = tabComponentList;
        setTabComponents([..._tabComponents]);
    }, [props.blockConfig.tabs]);

    React.useEffect(() => {
        if (!tabComponents || tabComponents.length == 0) {
            return;
        }
        let activeTabPresent = false;
        let firstVisibleTab, temp;
        if (activeTab != undefined) {
            // Check to see if the active tab is visible
            for (let i = 0; i < tabComponents.length; i++) {
                temp = tabComponents[i];
                if (temp.tab && temp.tab.id != activeTab) {
                    // Not active tab
                    if (temp.visible && !firstVisibleTab) {
                        // Save a reference to the first visible tab
                        firstVisibleTab = temp.tab;
                    }
                    continue;
                }
                if (temp.visible == false) {
                    // Active tab is not visible
                    continue;
                }
                // Active tab is visible
                activeTabPresent = true;
                break;
            }
            if (!activeTabPresent) {
                // Current active tab is no longer visible => Set first visible tab as active
                handleTabClick(firstVisibleTab, false);
            }
        } else {
            // No current active tab => Set one by default
            const hash = location && location.hash && location.hash.replace('#', '');
            for (let i = 0; i < tabComponents.length; i++) {
                temp = tabComponents[i];
                if (!temp.tab || temp.visible == false) {
                    // Tab is not visible
                    continue;
                }
                if (!hash || temp.tab.anchorName == hash) {
                    // Found tab for hash or there is no hash (in which case, set first visible tab active)
                    activeTabPresent = true;
                    setActiveTab(temp.tab.id);
                    break;
                } else if (!firstVisibleTab) {
                    // Save a reference to the first visible tab
                    firstVisibleTab = temp.tab;
                }
            }
            if (!activeTabPresent) {
                // Set first visible tab active
                handleTabClick(firstVisibleTab, false);
            }
        }
    }, [tabComponents]);

    const [visible, setVisible] = React.useState(true);
    React.useEffect(() => {
        if (!tabComponents || props.builder) {
            return;
        }
        let temp;
        for (let i = 0; i < tabComponents.length; i++) {
            temp = tabComponents[i];
            if (temp.visible) {
                return;
            }
            if (temp && temp.components) {
                for (let id in temp.components) {
                    if (temp.components[id].pending || temp.components[id].visible) {
                        return;
                    }
                }
            }
        }
        setVisible(false);
    }, [tabComponents])

    const _handleComponentStateUpdate = (tabId, id, data) => setTimeout(() => handleComponentStateUpdate(tabId, id, data), 500)
    const handleComponentStateUpdate = React.useCallback((tabId, id, data) => {
        if (!_tabComponents) {
            _handleComponentStateUpdate(tabId, id, data);
            return;
        }
        let temp;
        for (let i = 0; i < _tabComponents.length; i++) {
            temp = _tabComponents[i];
            if (!temp.tab || temp.tab.id != tabId) {
                continue;
            }
            if (!temp.components || !temp.components[id]) {
                _handleComponentStateUpdate(tabId, id, data);
            } else {
                // Update the block component state
                temp.components[id].pending = false;
                temp.components[id].visible = data && data.length > 0;

                // Update the tab-level visibility state
                temp.visible = false;
                for (let componentId in temp.components) {
                    if (!temp.components[componentId].visible) {
                        continue;
                    }
                    temp.visible = true;
                }
                setTabComponents([..._tabComponents]);
            }
            break;
        }
    }, [_tabComponents]);

    // Title
    const parseContent = (props, tokenizedString) => {
        if (!tokenizedString) {
            return '';
        }
        const data = props.data || {};
        let design;
        switch (props.pageType) {
            case PageType.ArtistDetail:
                if (data.artist) {
                    design = Token.parseTokenizedString(tokenizedString, (keyword, options) => Token.parsing.artist({ artist: data.artist, wrapContent: false }, keyword, options));
                }
                break;
            case PageType.Custom:
                design = tokenizedString;
                break;
            case PageType.ExhibitionDetail:
                if (data.show) {
                    design = Token.parseTokenizedString(tokenizedString, (keyword, options) => Token.parsing.show({ show: data.show, wrapContent: false }, keyword, options));
                }
                break;
        }
        if (Utilities.isHTMLContentEmpty(design)) {
            return '';
        }
        return design;
    }
    const [title, setTitle] = React.useState(undefined);
    React.useEffect(() => {
        setTitle(parseContent(props, props.blockConfig.layoutTabs.title));
    }, [props.blockConfig.layoutTabs.title]);

    if (!visible) {
        return null;
    }

    // Builder checks
    let BuilderTabSelectionDropDown;
    const hasNonEmptyTabs = tabComponents && tabComponents.length > 0;
    if (props.builder) {
        if (!hasNonEmptyTabs) {
            // Builder empty state
            return (
                <React.Fragment>
                    <TabSelectionDropDown disabled={true} onBlockMove={props.onBlockMove} />
                    <EmptyContentPlaceholder>You haven't added any tabs with content yet. Once you do, you'll see them here.</EmptyContentPlaceholder>
                </React.Fragment>
            );
        }
        if (tabComponents) {
            const handleBuilderTabSelection = (tabId) => {
                if (props.onLayoutContainerTabSelection) {
                    props.onLayoutContainerTabSelection(tabId);
                }
                setActiveTab(tabId);
            }
            BuilderTabSelectionDropDown = (
                <TabSelectionDropDown tab={activeTab} onBlockMove={props.onBlockMove} onSelect={handleBuilderTabSelection}>
                    {tabComponents.map((d, i) => <option key={d.tab && d.tab.id} value={d.tab && d.tab.id}>{d.tab && d.tab.display || `Tab ${i + 1}`}</option>)}
                </TabSelectionDropDown>
            )
        }
    }

    // Tab Styles
    let activeTabColor, tabTitleSpacing, spacing;
    let alignment = 'center';
    let styleSeparator = "";
    let styleTab = {};
    let styleTabs = {};
    let styleTabsContainer = {};
    if (props.blockConfig && props.blockConfig.layoutTabs) {
        styleTabs = {};
        Block.mapStyleConfigProps(styleTabs, props.blockConfig.layoutTabs, (propName, propValue, propUnit) => {
            switch (propName) {
                case 'alignment':
                    alignment = propValue;
                    return true;
                case 'alignmentContainer':
                case 'alignmentTitle':
                case 'design':
                    return true;
                case 'colorActive':
                    activeTabColor = propValue;
                    return true;
                case 'color':
                case 'fontFamily':
                case 'fontWeight':
                    if (!props.tabsDesign) {
                        styleTab[propName] = propValue;
                    }
                    return true;
                case 'fontSize':
                case 'letterSpacing':
                    if (!props.tabsDesign && propValue && propUnit) {
                        styleTab[propName] = `${propValue}${propUnit}`
                    }
                    return true;
                case 'separatorColor':
                    if (props.tabsDesign) {
                        // V2
                        styleSeparator += `background-color:${propValue};`;
                    } else {
                        styleTab.borderRightColor = propValue;
                    }
                    return true;
                case 'separatorHeight':
                    if (propValue && propUnit) {
                        if (props.tabsDesign) {
                            // V2
                            styleSeparator += `height:${propValue}${propUnit};`;
                        } else {
                            styleTab.lineHeight = `${propValue}${propUnit}`
                        }
                    }
                    return true;
                case 'separatorWidth':
                    if (propValue && propUnit) {
                        if (props.tabsDesign) {
                            // V2
                            styleSeparator += `width:${propValue}${propUnit};`;
                        } else {
                            styleTab.borderRightWidth = `${propValue}${propUnit}`
                        }
                    }
                    return true;
                case 'lineSpacing':
                    if (propValue == undefined || propUnit == undefined) {
                        return true;
                    }
                    if (props.tabsDesign) {
                        // V2
                        spacing = propValue;
                    } else {
                        let value = propValue / 2;
                        value = `${value}${propUnit}`;
                        styleTab.marginRight = value;
                        styleTab.paddingRight = value;
                    }
                    return true;
                case 'maxWidth':
                    if (propValue && propUnit) {
                        styleTabsContainer = { maxWidth: `${propValue}${propUnit}` };
                    }
                    return true;
                case 'paddingRightTitle':
                    if (propValue && propUnit) {
                        tabTitleSpacing = `${propValue}${propUnit}`
                    }
                    return true;
                case 'title':
                    return true;
            }
        });
        styleTabsContainer = Block.updateAlignment(styleTabsContainer, props.blockConfig.layoutTabs.alignmentContainer);
    }
    if (!props.tabsDesign) {
        if (!styleTab.borderRightWidth) {
            styleTab.borderRightWidth = '1px';
        }
        if (!styleTab.borderRightColor) {
            styleTab.borderRightColor = '#c4c4c4';
        }
    }

    // Header Content (Tabs, Title)
    let headerContent = (
        <TabsContainer
            activeTabColor={activeTabColor}
            alignment={alignment}
            builder={props.builder}
            design={props.tabsDesign}
            spacing={spacing}
            styleSeparator={styleSeparator}
            styleTab={styleTab}
            tab={activeTab}
            tabComponent={TabV2}
            tabs={tabComponents}
            onTabClick={handleTabClick} />
    );
    if (props.blockConfig) {
        if (title && title.length > 0) {
            const alignmentTitle = props.blockConfig.layoutTabs && props.blockConfig.layoutTabs.alignmentTitle;
            const _title = <div className="richText tabs__title"><div dangerouslySetInnerHTML={{ __html: title }} /></div>
            switch (alignment) {
                case 'left':
                    switch (alignmentTitle) {
                        case 'left':
                            headerContent = (
                                <React.Fragment>
                                    {_title}
                                    {
                                        tabTitleSpacing &&
                                        <div style={{ width: tabTitleSpacing }} />
                                    }
                                    {headerContent}
                                    <div style={{ flex: 1 }} />
                                </React.Fragment>
                            );
                            break;
                        case 'right':
                            headerContent = (
                                <React.Fragment>
                                    {headerContent}
                                    <div style={{ flex: 1 }} />
                                    {_title}
                                </React.Fragment>
                            );
                            break;
                        default:
                            headerContent = (
                                <div className="tabs__wrapper--centered">
                                    <div className="tab__title">
                                        {_title}
                                    </div>
                                    <div className="tabs">
                                        {headerContent}
                                        <div style={{ flex: 1 }} />
                                    </div>
                                </div>
                            );
                            break;
                    }
                    break;
                case 'right':
                    switch (alignmentTitle) {
                        case 'left':
                            headerContent = (
                                <React.Fragment>
                                    {_title}
                                    <div style={{ flex: 1 }} />
                                    {headerContent}
                                </React.Fragment>
                            );
                            break;
                        case 'right':
                            headerContent = (
                                <React.Fragment>
                                    {headerContent}
                                    <div style={{ flex: 1 }} />
                                    {
                                        tabTitleSpacing &&
                                        <div style={{ width: tabTitleSpacing }} />
                                    }
                                    {_title}
                                </React.Fragment>
                            );
                            break;
                        default:
                            headerContent = (
                                <div className="tabs__wrapper--centered">
                                    <div className="tab__title">
                                        {_title}
                                    </div>
                                    <div className="tabs">
                                        <div style={{ flex: 1 }} />
                                        {headerContent}
                                    </div>
                                </div>
                            );
                            break;
                    }
                    break;
                default:
                    switch (alignmentTitle) {
                        case 'left':
                            headerContent = (
                                <div className="tabs__wrapper--centered">
                                    <div style={{ textAlign: 'left' }}>
                                        {_title}
                                    </div>
                                    <div className="tabs">
                                        <div style={{ flex: 1 }} />
                                        {headerContent}
                                        <div style={{ flex: 1 }} />
                                    </div>
                                </div>
                            );
                            break;
                        case 'right':
                            headerContent = (
                                <div className="tabs__wrapper--centered">
                                    <div style={{ textAlign: 'right' }}>
                                        {_title}
                                    </div>
                                    <div className="tabs">
                                        <div style={{ flex: 1 }} />
                                        {headerContent}
                                        <div style={{ flex: 1 }} />
                                    </div>
                                </div>
                            );
                            break;
                        default:
                            headerContent = (
                                <div className="tabs__wrapper--centered">
                                    <div className="tab__title">
                                        {_title}
                                    </div>
                                    {
                                        tabTitleSpacing &&
                                        <div style={{ height: tabTitleSpacing }} />
                                    }
                                    <div className="tabs">
                                        <div style={{ flex: 1 }} />
                                        {headerContent}
                                        <div style={{ flex: 1 }} />
                                    </div>
                                </div>
                            );
                            break;
                    }
                    break;
            }
        } else {
            switch (alignment) {
                case 'left':
                    headerContent = (
                        <React.Fragment>
                            {headerContent}
                            <div style={{ flex: 1 }} />
                        </React.Fragment>
                    );
                    break;
                case 'right':
                    headerContent = (
                        <React.Fragment>
                            <div style={{ flex: 1 }} />
                            {headerContent}
                        </React.Fragment>
                    );
                    break;
                default:
                    headerContent = (
                        <React.Fragment>
                            <div style={{ flex: 1 }} />
                            {headerContent}
                            <div style={{ flex: 1 }} />
                        </React.Fragment>
                    );
                    break;
            }
        }
    }

    // Container Styles
    let backgroundColor = props.backgroundColor;
    const layoutStyles = Block.mapLayoutStyleConfigPropsWithPageKit({}, props, { configKey: 'layoutContent' });
    const wrapperStyle = {}
    if (layoutStyles.style.backgroundColor) {
        backgroundColor = layoutStyles.style.backgroundColor;
        wrapperStyle.backgroundColor = layoutStyles.style.backgroundColor;
        layoutStyles.style.backgroundColor = undefined;
    }
    if (layoutStyles.style.marginLeft) {
        wrapperStyle.marginLeft = layoutStyles.style.marginLeft;
        layoutStyles.style.marginLeft = undefined;
    }
    if (layoutStyles.style.marginRight) {
        wrapperStyle.marginRight = layoutStyles.style.marginRight;
        layoutStyles.style.marginRight = undefined;
    }
    if (layoutStyles.style.paddingLeft) {
        wrapperStyle.paddingLeft = layoutStyles.style.paddingLeft;
        layoutStyles.style.paddingLeft = undefined;
    }
    if (layoutStyles.style.paddingRight) {
        wrapperStyle.paddingRight = layoutStyles.style.paddingRight;
        layoutStyles.style.paddingRight = undefined;
    }

    const handleAsyncLoad = (tabId, blockId, data) => {
        console.log(tabId, blockId, data);
        if (!props.builder) {
            handleComponentStateUpdate(tabId, blockId, data);
        }
    }
    return (
        <React.Fragment>
            {BuilderTabSelectionDropDown}
            <div className="cont--kit__wrapper" style={layoutStyles.containerStyle}>
                <div className="cont--tabs__wrapper cont--kit__content" style={wrapperStyle}>
                    <div className="cont--tabs__controls" style={styleTabsContainer}>
                        <div className="tabs__wrapper" style={styleTabs}>
                            {headerContent}
                        </div>
                    </div>
                    <div className="cont--tabs__content" style={layoutStyles.style}>
                        {
                            tabComponents &&
                            tabComponents.map((d) => (
                                <ContentContainer
                                    {...props}
                                    key={d.tab && d.tab.id}
                                    active={d.tab && activeTab == d.tab.id}
                                    backgroundColor={backgroundColor}
                                    contentConfig={d.tab && d.tab.tabContent}
                                    tabId={d.tab && d.tab.id}
                                    onAsyncLoad={handleAsyncLoad} />
                            ))
                        }
                        {
                            props.builder &&
                            !hasNonEmptyTabs &&
                            <EmptyPlaceholder />
                        }
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}
const TabsV1 = { Component: TabsV1Component }

// region TABS V2

function TabsV2Component(props) {
    let tabsDesign;
    if (props.blockConfig && props.blockConfig.layoutTabs) {
        tabsDesign = props.blockConfig.layoutTabs.design;
    }
    if (!tabsDesign) {
        let color;
        if (props.config && props.config.colors) {
            color = props.config.colors.accent;
        }
        tabsDesign = Defaults.Block[Name.Block.Tabs](color);
    }
    return <TabsV1Component {...props} tabsDesign={tabsDesign} />
}
const TabsV2 = { Component: TabsV2Component }

// region TAB CONTENT

function ContentContainer(props) {
    const contentConfig = props.contentConfig || {};
    let blocks = [];
    if (contentConfig.desktop && contentConfig.desktop.blocks) {
        blocks = contentConfig.desktop && contentConfig.desktop.blocks;
    }
    let active = false;
    let className = 'tabs__content cont--whitelabel';
    if (props.active || props.editor) {
        active = true;
        className += ' current';
    }
    let alignment;
    const handleMapStyleConfigComplete = (style) => Block.updateAlignment(style, alignment)
    const handleMapStyleConfigProp = (_, propName, propValue) => {
        switch (propName) {
            case 'textAlign':
                alignment = propValue;
                return true;
        }
    }
    const backgroundColor = Layout.backgroundColorV2(props, contentConfig);
    const blockPropsHandler = (d) => {
        if (!d.type) {
            return;
        }
        const blockProps = { backgroundColor, editor: false };
        switch (d.type.value) {
            case Name.Block.Artist.Inventory.ListV1:
            case Name.Block.Artist.Inventory.ListV2:
            case Name.Block.Artist.Inventory.ListV3:
            case Name.Block.Inventory.ListV1:
            case Name.Block.Inventory.ListV2:
            case Name.Block.Inventory.ListV3:
            case Name.Block.Inventory.RelatedV1:
            case Name.Block.Inventory.RelatedV2:
            case Name.Block.Inventory.RelatedV3:
            case Name.Block.Show.Inventory.ListV1:
            case Name.Block.Show.Inventory.ListV2:
            case Name.Block.Show.Inventory.ListV3:
                blockProps.active = active;
            // Intentional fallthrough
            case Name.Block.Artist.Blog.List:
            case Name.Block.Artist.List:
            case Name.Block.Artist.Media.List:
            case Name.Block.Artist.Show.List.Grid:
            case Name.Block.Artist.Show.List.Parallax:
            case Name.Block.Artist.Show.List.Slideshow:
            case Name.Block.Artist.Show.List.SlideshowFullBackground:
            case Name.Block.Blog.List:
            case Name.Block.Inventory.Blog.List:
            case Name.Block.Inventory.Media.List:
            case Name.Block.Inventory.RecentlyViewed.List:
            case Name.Block.Media.List:
            case Name.Block.Show.Artist.List:
            case Name.Block.Show.Blog.List:
            case Name.Block.Show.List.Grid:
            case Name.Block.Show.List.Parallax:
            case Name.Block.Show.List.Slideshow:
            case Name.Block.Show.List.SlideshowFullBackground:
            case Name.Block.Show.Media.List:
                blockProps.onAsyncLoad = (blockId, data) => {
                    if (!props.builder && props.onAsyncLoad) {
                        props.onAsyncLoad(props.tabId, blockId, data)
                    }
                };
                break;
        }
        return blockProps;
    }
    return (
        <EditorContainer
            {...props}
            backgroundColor={backgroundColor}
            blockPropsHandler={blockPropsHandler}
            blocks={blocks}
            className={className}
            containerProps={contentConfig.desktop && contentConfig.desktop.page}
            inCell={true}
            onContainerProps={props.onPageProps}
            onMapStyleConfigComplete={handleMapStyleConfigComplete}
            onMapStyleConfigProp={handleMapStyleConfigProp} />
    );
}

// region LEGACY

function _TabsLegacy(props) {
    const _tabs = props.blockConfig.tabs || [];
    const [data, setData] = React.useState(props.builder ? props.data : undefined);
    const [tabs, setTabs] = React.useState(props.builder ? _tabs : undefined);
    const [activeTab, setActiveTab] = React.useState(undefined);

    React.useEffect(() => {
        if (!props.data) {
            return;
        }
        const nonEmptyData = [];
        const nonEmptyTabs = [];
        _tabs.forEach((t) => {
            let shouldExit = false;
            let d;
            for (let i = 0; i < props.data.length; i++) {
                d = props.data[i];
                if (d.tabId != t.tabId || !d.blocks) {
                    continue;
                }
                let temp;
                for (let j = 0; j < d.blocks.length; j++) {
                    temp = d.blocks[j];
                    if (Types.Block.isComponentEmpty(temp, props.legacyData)) {
                        continue;
                    }
                    nonEmptyData.push(d);
                    nonEmptyTabs.push(t);
                    shouldExit = true;
                    break;
                }
                if (shouldExit) {
                    break;
                }
            }
        });
        setData(nonEmptyData);
        setTabs(nonEmptyTabs);
        if (nonEmptyTabs.length > 0) {
            setActiveTab(nonEmptyTabs[0].tabId)
        }
    }, []);

    if (props.builder && _tabs.length == 0) {
        return <EmptyContentPlaceholder>You haven't added any tabs yet. Once you do, you'll see them here.</EmptyContentPlaceholder>
    }
    return (
        <React.Fragment>
            <TabsContainer builder={props.builder}>
                {
                    tabs &&
                    tabs.map((d) => <Tab key={d.tabId} active={activeTab == d.tabId} display={d.name} value={d.tabId} onClick={setActiveTab} />)
                }
            </TabsContainer>
            {
                data &&
                data.map((d) => (
                    <ContentWrapper key={d.tabId} active={activeTab == d.tabId}>
                        <Content
                            builder={props.builder}
                            config={props.config}
                            data={d.blocks}
                            frameDoc={props.frameDoc}
                            handlerUrl={props.handlerUrl}
                            legacy={props.legacy}
                            pageType={props.pageType}
                            querySub={props.querySub} />
                    </ContentWrapper>
                ))
            }
        </React.Fragment>
    );
}
const TabsLegacy = withConfig(_TabsLegacy);
const Legacy = { Component: TabsLegacy };

function ContentWrapper(props) {
    let className = 'tabs__content';
    if (props.active) {
        className += ' current';
    }
    return (
        <div className={className}>
            {props.children}
        </div>
    )
}

function Content(props) {
    const data = props.data || [];
    const blocks = [];
    data.forEach(function (d, i) {
        if (!d.type) {
            return;
        }
        const componentData = Types.Block.getComponentAndData(d, props.pageType);
        if (!componentData || !componentData.component) {
            return;
        }
        blocks.push(
            <componentData.component
                key={i}
                blockConfig={d.config}
                builder={props.builder}
                config={props.config}
                context={d.context}
                data={componentData.data}
                frameDoc={props.frameDoc}
                handlerUrl={props.handlerUrl}
                legacy={props.legacy}
                querySub={props.querySub} />
        );
    });
    return (
        <React.Fragment>
            {blocks}
        </React.Fragment>
    )
}

export {
    ContentContainer,
    TabsV2,

    // DEPRECATED
    TabsV1,
    Legacy
};